import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logPageView } from '../utils/auditUtils';
import logger from '../utils/logger';

// Client-related routes that should be tracked
const CLIENT_ROUTES = [
  '/client-list',
  '/add-client',
  '/mhop',
  '/behavioral-health'
];

// Check if a route is client-related
const isClientRoute = (pathname) => {
  // Check exact matches
  if (CLIENT_ROUTES.includes(pathname)) {
    return true;
  }

  // Check if it's a client profile page
  if (pathname.startsWith('/client/')) {
    return true;
  }

  return false;
};

/**
 * Hook to automatically track page views for client-related pages
 * @param {Object} options - Configuration options
 * @param {boolean} options.trackAllPages - Track all pages, not just client-related (default: false)
 * @param {Array} options.additionalRoutes - Additional routes to track
 */
export const usePageViewTracking = (options = {}) => {
  const { trackAllPages = false, additionalRoutes = [] } = options;
  const location = useLocation();

  useEffect(() => {
    const trackPage = async () => {
      try {
        const { pathname, search } = location;
        const fullPath = `${pathname}${search}`;

        // Determine if we should track this page
        const shouldTrack = trackAllPages ||
                           isClientRoute(pathname) ||
                           additionalRoutes.some(route => pathname.startsWith(route));

        if (!shouldTrack) {
          logger.debug('Skipping page view tracking', { pathname });
          return;
        }

        // Extract route parameters for metadata
        const metadata = {
          pathname,
          search,
          fullPath,
          timestamp: new Date().toISOString()
        };

        // Extract client ID if it's a client profile page
        if (pathname.startsWith('/client/')) {
          const clientId = pathname.split('/client/')[1]?.split('?')[0];
          if (clientId) {
            metadata.clientId = clientId;
          }
        }

        // Log the page view
        await logPageView(fullPath, metadata);

        logger.debug('Page view tracked', { pathname, fullPath });
      } catch (error) {
        // Don't throw - page tracking failures shouldn't break the app
        logger.error('Failed to track page view', {
          error: error.message,
          pathname: location.pathname
        });
      }
    };

    // Small delay to ensure component is fully mounted
    const timeoutId = setTimeout(trackPage, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, trackAllPages, additionalRoutes]);
};

export default usePageViewTracking;

